import React, { useCallback, useEffect, useState } from 'react';
import { History, RefreshCw, ChevronLeft } from 'lucide-react';
import { digestApi, type DailyDigest } from '@/services/api/agentApi';
import { DigestMessage } from './DigestMessage';
import { Button } from '@/components/ui/button';

interface Props {
  onClose?: () => void;
}

export const PastMorningsBrowser: React.FC<Props> = ({ onClose }) => {
  const [digests, setDigests] = useState<DailyDigest[]>([]);
  const [selected, setSelected] = useState<DailyDigest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await digestApi.list(undefined, undefined, 60, 0);
      setDigests(res.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  if (selected) {
    return (
      <div className="p-4">
        <Button size="sm" variant="ghost" onClick={() => setSelected(null)} className="mb-4">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <DigestMessage digest={selected} />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Past Mornings</h2>
          <span className="text-xs text-muted-foreground">
            Every brief your agent has written for you
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onClose && <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}
          <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 mb-3">Failed to load: {error}</div>}
      {!loading && digests.length === 0 && (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No past mornings yet. The first one will appear after your first overnight dream.
        </div>
      )}

      <div className="space-y-2">
        {digests.map(d => {
          const date = new Date(d.for_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          const preview = d.narrative_text.slice(0, 180);
          return (
            <button
              key={d.digest_id}
              onClick={() => setSelected(d)}
              className="w-full text-left border rounded-md p-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{date}</span>
                <span className="text-xs text-muted-foreground">
                  {d.overdue_count > 0 ? `${d.overdue_count} overdue · ` : ''}
                  {d.promotions_pending > 0 ? `${d.promotions_pending} pending` : ''}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{preview}…</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
